import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { sendGenieMessage } from "../../lib/api";

const GREETING = {
  role: "assistant",
  content: "Hello, I'm Genie. How may I assist you today?",
};

// First-landing highlight overlay timings.
const HIGHLIGHT_DELAY_MS = 10000;
const HIGHLIGHT_AUTOHIDE_MS = 9000;
const HIGHLIGHT_STORAGE_KEY = "geniex_highlight_seen";

function genSessionId() {
  let s = localStorage.getItem("geniex_session");
  if (!s) {
    s = `gx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("geniex_session", s);
  }
  return s;
}

const SUGGESTIONS = [
  "What is the Dynamic Series?",
  "I have an existing 3BHK — what fits me?",
  "Approximate cost for a villa?",
  "Do you serve Vijayawada?",
];

export default function AskGenieDock() {
  const location = useLocation();
  const hiddenOnRoute = location.pathname.startsWith("/checkout");
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const sessionRef = useRef(genSessionId());
  const scrollerRef = useRef(null);

  // 10s after first landing, reveal a luxury highlight halo around the
  // launcher. Auto-hides after a few seconds or on any click.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(HIGHLIGHT_STORAGE_KEY)) return;
    const t1 = setTimeout(() => setHighlight(true), HIGHLIGHT_DELAY_MS);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => dismissHighlight(), HIGHLIGHT_AUTOHIDE_MS);
    return () => clearTimeout(t);
     
  }, [highlight]);

  const dismissHighlight = () => {
    setHighlight(false);
    try {
      localStorage.setItem(HIGHLIGHT_STORAGE_KEY, "1");
    } catch (_) {
      // ignore quota / privacy mode
    }
  };

  // Greeting must appear only after the user opens the dock.
  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([GREETING]);
    }
  }, [open, msgs.length]);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [msgs, open]);

  const openDock = () => {
    dismissHighlight();
    setOpen(true);
  };

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: t }]);
    setBusy(true);
    try {
      const res = await sendGenieMessage(sessionRef.current, t);
      setMsgs((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMsgs((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I am momentarily unavailable. Please try again in a moment.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Hidden entirely during checkout to avoid overlapping the payment flow */}
      {!hiddenOnRoute && (
        <>
          {/* Floating Genie face launcher (bottom-right) */}
          {!open && (
        <div className="fixed right-5 bottom-5 sm:right-7 sm:bottom-7 z-[80] flex flex-col items-end gap-3">
          {/* Highlight overlay: appears 10s after landing */}
          {highlight && (
            <div
              data-testid="genie-highlight-overlay"
              className="genie-highlight-card"
              role="dialog"
              aria-label="Meet Genie, your concierge"
            >
              <button
                type="button"
                onClick={dismissHighlight}
                aria-label="Dismiss"
                data-testid="genie-highlight-dismiss"
                className="absolute top-2 right-2 w-6 h-6 rounded-full text-mute hover:text-gold flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
              <p className="eyebrow !text-gold !text-[10px]">Meet Genie</p>
              <p className="font-display text-white text-[15px] leading-snug mt-1.5">
                Your private concierge — ask anything about your home.
              </p>
              <p className="text-[11.5px] text-dim mt-1.5">Tap to begin.</p>
              <span className="genie-highlight-arrow" aria-hidden />
            </div>
          )}

          <button
            onClick={openDock}
            data-testid="genie-launcher"
            aria-label="Open Ask Genie"
            className={`genie-face-btn ${
              highlight ? "is-highlighted" : ""
            }`}
          >
            <img
              src="/genie-face.png"
              alt="Genie"
              draggable={false}
              className="genie-face-img"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="genie-face-ring" aria-hidden />
            <span className="genie-face-label">Ask Genie</span>
          </button>
        </div>
      )}

      {/* Panel */}
      <div
        data-testid="genie-panel"
        className={`fixed z-[90] transition-all duration-500 ease-out ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-6 pointer-events-none"
        }
        left-3 right-3 bottom-3
        sm:left-auto sm:right-6 sm:bottom-6 sm:w-[400px]
        h-[78vh] sm:h-[600px] max-h-[760px]
        flex flex-col overflow-hidden rounded-[26px] border border-line shadow-2xl
        bg-[rgba(12,12,16,0.92)] backdrop-blur-2xl`}
      >
        <div className="absolute top-0 left-0 right-0 gold-line opacity-70" />
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-line flex items-start gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#E2C07A] to-[#8E7338] flex items-center justify-center shrink-0 shadow-[0_0_24px_rgba(201,169,97,0.35)] border border-gold/40">
            <img
              src="/genie-face.png"
              alt="Genie"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.replaceWith(
                  Object.assign(document.createElement("span"), {
                    className: "text-[#0a0a0c]",
                    textContent: "G",
                  })
                );
              }}
            />
          </div>
          <div className="flex-1">
            <p className="font-display text-[15px] leading-tight">Genie</p>
            <p className="text-[11px] text-mute tracking-wide">
              Your private GenieX concierge
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close Genie"
            data-testid="genie-close"
            className="w-8 h-8 rounded-full border border-line hover:border-gold hover:text-gold text-dim flex items-center justify-center transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-4"
        >
          {msgs.map((m, i) => (
            <div
              key={i}
              data-testid={`genie-msg-${m.role}`}
              className={
                m.role === "assistant" ? "flex gap-3" : "flex justify-end"
              }
            >
              {m.role === "assistant" && (
                <span className="w-7 h-7 rounded-full overflow-hidden bg-[var(--gx-elev)] border border-gold/30 flex items-center justify-center shrink-0">
                  <img
                    src="/genie-face.png"
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.replaceWith(
                        Object.assign(document.createElement("span"), {
                          textContent: "✦",
                        })
                      );
                    }}
                  />
                </span>
              )}
              <div
                className={`max-w-[82%] px-4 py-3 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "assistant"
                    ? "bg-[var(--gx-surface-2)] border border-line text-white rounded-tl-sm"
                    : "bg-gold text-[#0a0a0c] rounded-tr-sm"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-3" data-testid="genie-typing">
              <span className="w-7 h-7 rounded-full bg-[var(--gx-elev)] border border-gold/30 flex items-center justify-center shrink-0">
                <Sparkles className="w-3 h-3 text-gold" />
              </span>
              <div className="px-4 py-3 rounded-2xl bg-[var(--gx-surface-2)] border border-line">
                <span className="flex gap-1.5 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold/70 animate-pulse" />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-gold/70 animate-pulse"
                    style={{ animationDelay: "150ms" }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-gold/70 animate-pulse"
                    style={{ animationDelay: "300ms" }}
                  />
                </span>
              </div>
            </div>
          )}
          {msgs.length <= 1 && !busy && (
            <div className="pt-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="genie-suggestion"
                  className="text-[11.5px] px-3 py-2 rounded-full border border-line text-dim hover:border-gold/60 hover:text-gold transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="border-t border-line px-3 py-3 flex items-center gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Genie anything…"
            data-testid="genie-input"
            className="flex-1 bg-transparent outline-none px-3 py-2.5 text-[14px] text-white placeholder:text-mute"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            data-testid="genie-send"
            className="w-10 h-10 rounded-full bg-gold text-[#0a0a0c] flex items-center justify-center disabled:opacity-40 hover:bg-[var(--gx-gold-bright)] transition-colors"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
          </button>
        </form>
      </div>
        </>
      )}
    </>
  );
}
